import type { InventoryDrone } from '@/lib/api/types';
import Fuse from 'fuse.js';
import type { DroneFilterKey } from './InvStore';
import { djiOnline } from './djiStatus';

function droneOnline(d: InventoryDrone): boolean {
  if (d.dji_status) return djiOnline(d);
  return d.custodian_status === 'online' || d.gcs_plugin_status === 'online';
}

/** AND across axes, OR within an axis — mockup `applyInvDroneFilter`. */
export function matchesFilters(d: InventoryDrone, f: Record<DroneFilterKey, boolean>): boolean {
  // An aircraft whose adapter has no chip of its own (no adapter_type, shown as
  // "—", or a type we don't list) falls under the "Other" chip instead of
  // bypassing the adapter filter.
  const adapterKey = `adapter:${d.personality}` as DroneFilterKey;
  const adapter = f[Object.hasOwn(f, adapterKey) ? adapterKey : 'adapter:Other'];

  // Protocol axis: `mavlink_v2` aircraft follow the "MAVLink v2" chip; any other
  // or missing protocol has no chip here, so this axis doesn't affect it.
  const protocol =
    d.adapter_protocol?.trim().toLowerCase() === 'mavlink_v2' ? f['protocol:MAVLink v2'] : true;

  const custodianTags: DroneFilterKey[] = [];
  if (d.custodian_status !== 'none') custodianTags.push('custodian:aircraft');
  if (d.gcs_plugin_status !== 'none') custodianTags.push('custodian:gcs');
  if (custodianTags.length === 0) custodianTags.push('custodian:none');
  const custodian = custodianTags.some((k) => f[k]);

  const state = f[droneOnline(d) ? 'state:online' : 'state:offline'];
  const dtwin = f[d.dtwin_sync === 'updated' ? 'dtwin:updated' : 'dtwin:outdated'];

  return adapter && protocol && custodian && state && dtwin;
}

/** Fuzzy-match keys, weighted toward the identifiers a user is most likely to
 *  type (the ticket's own example, "AIRTL", is a `drone_id`/`display_name`). */
const SEARCH_KEYS: { name: keyof InventoryDrone; weight: number }[] = [
  { name: 'drone_id', weight: 2 },
  { name: 'display_name', weight: 2 },
  { name: 'serial_number', weight: 1 },
  { name: 'registration', weight: 1 },
  { name: 'ec_callsign', weight: 1 },
  { name: 'ec_tc_reg', weight: 1 },
  { name: 'brand', weight: 0.5 },
  { name: 'model', weight: 0.5 },
  { name: 'drone_type', weight: 0.5 },
];

/** Chip filters (AND) first, then a fuzzy search (typo-tolerant, not plain
 *  substring) over the result — Custodian has no server-side search, so this
 *  runs entirely against the already-fetched fleet. */
export function filterInventoryDrones(
  drones: InventoryDrone[],
  filters: Record<DroneFilterKey, boolean>,
  searchTerm: string,
): InventoryDrone[] {
  const afterChipFilters = drones.filter((d) => matchesFilters(d, filters));
  const term = searchTerm.trim();
  if (!term) return afterChipFilters;

  const fuse = new Fuse(afterChipFilters, {
    keys: SEARCH_KEYS,
    threshold: 0.35,
    ignoreLocation: true,
  });
  return fuse.search(term).map((r) => r.item);
}
